import { Filter, X } from 'lucide-react'
import { useFormContext } from 'react-hook-form'

import { cn } from '@/lib/utils'

import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'

import { SelectInput, DateRangePicker, TextInput } from './inputs'

export type FilterFieldType = 'text' | 'select' | 'dateRange'

export interface FilterField {
    name: string
    label: string
    type: FilterFieldType
    placeholder?: string
    options?: { label: string; value: any }[]
    className?: string
}

interface AdvancedFilterProps {
    fields: FilterField[]
    onClear?: () => void
    activeFiltersCount?: number
}

export const AdvancedFilter = ({ fields, onClear, activeFiltersCount = 0 }: AdvancedFilterProps) => {
    const { control, resetField } = useFormContext()

    const handleClearAll = () => {
        fields.forEach((field) => {
            resetField(field.name)
        })
        onClear?.()
    }

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button variant="outline" className="relative">
                    <Filter className="mr-2 h-4 w-4" />
                    Filters
                    {activeFiltersCount > 0 && (
                        <Badge
                            variant="secondary"
                            className="ml-1 grid size-5 place-items-center rounded-full bg-primary text-xs font-medium text-primary-foreground"
                        >
                            {activeFiltersCount}
                        </Badge>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[calc(100vw-32px)] sm:w-[440px] p-0" align="end">
                <div className="p-4 space-y-4 max-h-[80vh] sm:max-h-[450px] overflow-y-auto">
                    <div className="flex items-center justify-between">
                        <h4 className="text-heading-md text-ink">Filters</h4>
                        {activeFiltersCount > 0 && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleClearAll}
                                className="h-8 px-2 text-muted-foreground hover:text-destructive">
                                <X className="mr-2 h-3 w-3" />
                                Clear
                            </Button>
                        )}
                    </div>
                    <Separator />

                    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
                        {fields.map((field) => (
                            <div key={field.name} className={cn("grid gap-2", field.className)}>
                                {field.type === 'text' && (
                                    <TextInput
                                        control={control}
                                        name={field.name}
                                        label={field.label}
                                        placeholder={field.placeholder}
                                    />
                                )}
                                {field.type === 'select' && (
                                    <SelectInput
                                        control={control}
                                        name={field.name}
                                        label={field.label}
                                        placeholder={field.placeholder}
                                        options={field.options || []}
                                    />
                                )}
                                {field.type === 'dateRange' && (
                                    <DateRangePicker
                                        control={control}
                                        name={field.name}
                                        label={field.label}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    )
}
